import './WashHighlight.css';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useReducedMotion } from '../a11y/useReducedMotion';
import { FALLBACK_PARTY, type Party, partyClass } from '../theme/parties';

/** A08 (§3.6.3): o véu some em `--dur-wash` (1.200 ms) e a camada sai do DOM aos 1.300 ms. */
export const WASH_REMOVE_MS = 1300;

export type WashLeaderKey = string | number | null;

export interface WashHighlightProps {
  /** Chave do líder da linha (ex.: número do candidato à frente); quando muda, dispara a lavagem. `null` = sem líder. */
  leaderKey: WashLeaderKey;
  /** Partido do líder atual: dá a cor do véu (`party-{n}`). Sem ele, a cor de reserva. */
  party?: Party | null;
  /** Replay: nunca dispara (§3.9). */
  replay?: boolean;
}

interface Wash {
  id: number;
  party: Party;
}

/**
 * Lavagem na troca de líder (§3.9, A08): camada sobre a linha (o dono tem `position: relative`), com
 * `color-mix(in oklch, var(--p-fill) 16%, transparent)` do novo líder, que some em `kf-wash` e sai do DOM aos 1.300 ms.
 * Não dispara na primeira renderização, no replay, com movimento reduzido nem quando a linha ganha ou perde o líder
 * (de/para `null`: não há troca). Puramente visual (`aria-hidden`); o anúncio é da `LiveRegion` (§15.3).
 */
export function WashHighlight({ leaderKey, party, replay = false }: WashHighlightProps) {
  const reduced = useReducedMotion();
  const prev = useRef<WashLeaderKey>(leaderKey);
  const seq = useRef(0);
  const [wash, setWash] = useState<Wash | null>(null);
  // O que vale é o estado no instante da troca; mudar a preferência ou o partido depois não redispara.
  const ctx = useRef({ party, replay, reduced });
  ctx.current = { party, replay, reduced };

  useEffect(() => {
    const before = prev.current;
    if (before === leaderKey) return;
    prev.current = leaderKey;
    const { party: p, replay: inReplay, reduced: still } = ctx.current;
    if (inReplay || still || before === null || leaderKey === null) {
      setWash(null);
      return;
    }
    seq.current += 1;
    setWash({ id: seq.current, party: p ?? FALLBACK_PARTY });
    const timer = setTimeout(() => setWash(null), WASH_REMOVE_MS);
    return () => clearTimeout(timer);
  }, [leaderKey]);

  if (!wash) return null;
  return (
    <span
      key={wash.id}
      class={`wash-highlight ${partyClass(wash.party)}`}
      data-testid="wash-highlight"
      data-dataviz=""
      aria-hidden="true"
    />
  );
}
